import { Badge } from '../../components/Badge'
import { Card } from '../../components/Card'
import { useDb } from '../../state/DbProvider'
import { getCurrentUser, isActiveUser } from '../../state/selectors'
import { formatJPY } from '../../utils/format'

export default function AdminDashboardPage() {
  const { db } = useDb()
  if (!db) return null

  const me = getCurrentUser(db)
  if (me.role !== 'J-Navi管理者') {
    return (
      <Card title="運営ダッシュボード" subtitle="このページは J-Navi 管理者向けです">
        <div className="px-5 py-4 text-sm text-slate-600">権限がありません。</div>
      </Card>
    )
  }

  const counts = {
    superAgencies: db.users.filter((user) => isActiveUser(user) && user.role === '総代理店').length,
    agencies: db.users.filter((user) => isActiveUser(user) && user.role === '代理店').length,
    connectors: db.users.filter((user) => isActiveUser(user) && user.role === 'コネクター').length,
    openDeals: db.deals.filter((deal) => !deal.locked && deal.status !== '失注').length,
    closedDeals: db.deals.filter((deal) => deal.locked).length,
  }

  let totalSales = 0
  let totalSuperAgency = 0
  let totalAgency = 0
  let totalConnector = 0
  let totalJnavi = 0
  let pendingRewards = 0
  const byProduct: Record<string, { sales: number; superAgency: number; agency: number; connector: number; jnavi: number }> = {}

  db.transactions.forEach((transaction) => {
    totalSales += transaction.saleAmountJPY
    const key = transaction.productSnapshot.name
    if (!byProduct[key]) {
      byProduct[key] = { sales: 0, superAgency: 0, agency: 0, connector: 0, jnavi: 0 }
    }
    byProduct[key].sales += transaction.saleAmountJPY

    transaction.allocations.forEach((allocation) => {
      if (allocation.recipientType === 'Jnavi取り分') {
        totalJnavi += allocation.amountJPY
        byProduct[key].jnavi += allocation.amountJPY
        return
      }

      if (allocation.userRole === '総代理店') {
        totalSuperAgency += allocation.amountJPY
        byProduct[key].superAgency += allocation.amountJPY
      } else if (allocation.userRole === '代理店') {
        totalAgency += allocation.amountJPY
        byProduct[key].agency += allocation.amountJPY
      } else if (allocation.userRole === 'コネクター') {
        totalConnector += allocation.amountJPY
        byProduct[key].connector += allocation.amountJPY
      }

      if (allocation.status === '未確定') {
        pendingRewards += allocation.amountJPY
      }
    })
  })

  const totals = { totalSales, totalSuperAgency, totalAgency, totalConnector, totalJnavi, pendingRewards, byProduct }

  return (
    <div className="space-y-6">
      <Card title="運営ダッシュボード" subtitle="商談・報酬・商品別売上の全体状況を確認できます。">
        <div className="grid gap-4 px-5 py-4 md:grid-cols-4">
          <SummaryCard label="総売上" value={totals.totalSales} />
          <SummaryCard label="総代理店報酬総額" value={totals.totalSuperAgency} />
          <SummaryCard label="代理店報酬総額" value={totals.totalAgency} />
          <SummaryCard label="コネクター報酬総額" value={totals.totalConnector} />
        </div>
        <div className="px-5 pb-4">
          <SummaryCard label="J-Navi 取り分" value={totals.totalJnavi} />
        </div>

        <div className="flex flex-wrap gap-2 px-5 pb-4 text-sm text-slate-700">
          <span className="inline-flex items-center gap-1">
            <Badge tone="blue">総代理店</Badge> {counts.superAgencies}社
          </span>
          <span className="inline-flex items-center gap-1">
            <Badge tone="blue">代理店</Badge> {counts.agencies}社
          </span>
          <span className="inline-flex items-center gap-1">
            <Badge tone="green">コネクター</Badge> {counts.connectors}人
          </span>
          <span className="inline-flex items-center gap-1">
            <Badge tone="amber">進行中</Badge> {counts.openDeals}件
          </span>
          <span className="inline-flex items-center gap-1">
            <Badge tone="slate">売上確定済み</Badge> {counts.closedDeals}件
          </span>
          <span className="inline-flex items-center gap-1">
            <Badge tone="amber">未確定報酬</Badge> {formatJPY(totals.pendingRewards)}
          </span>
        </div>
      </Card>

      <Card title="商品別サマリー" subtitle="商品ごとの売上と配分状況です。">
        <div className="overflow-x-auto px-5 py-4">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-4 py-3">商品</th>
                <th className="px-4 py-3">売上</th>
                <th className="px-4 py-3">総代理店</th>
                <th className="px-4 py-3">代理店</th>
                <th className="px-4 py-3">コネクター</th>
                <th className="px-4 py-3">J-Navi</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(totals.byProduct).map(([productName, summary]) => (
                <tr key={productName} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-semibold text-slate-900">{productName}</td>
                  <td className="px-4 py-3">{formatJPY(summary.sales)}</td>
                  <td className="px-4 py-3">{formatJPY(summary.superAgency)}</td>
                  <td className="px-4 py-3">{formatJPY(summary.agency)}</td>
                  <td className="px-4 py-3">{formatJPY(summary.connector)}</td>
                  <td className="px-4 py-3">{formatJPY(summary.jnavi)}</td>
                </tr>
              ))}
              {Object.keys(totals.byProduct).length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-sm text-slate-500" colSpan={6}>
                    まだ売上データはありません。
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

function SummaryCard(props: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500">{props.label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{formatJPY(props.value)}</div>
    </div>
  )
}
